import { downloadPictureForAnalysis } from '../photoQuality/orderScanner';
import { detectFaceGeometry } from '../photoQuality/faceLandmarker';
import { extractFaceEmbedding } from './sface';

const PERSON_SCAN_MAX_EDGE = 1600;
const PERSON_SCAN_MIN_EDGE = 720;
const MIN_FACE_EDGE = 48;
const TILE_SCALE = 2;

async function decodeImage(file) {
  if (typeof createImageBitmap === 'function') return createImageBitmap(file, { imageOrientation: 'from-image' });
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
    image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('图片解码失败')); };
    image.src = url;
  });
}

function getPersonScanDimensions(sourceWidth, sourceHeight) {
  const longestEdge = Math.max(sourceWidth, sourceHeight);
  const shortestEdge = Math.min(sourceWidth, sourceHeight);
  const scale = Math.min(
    PERSON_SCAN_MAX_EDGE / longestEdge,
    Math.max(1, PERSON_SCAN_MIN_EDGE / shortestEdge)
  );
  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale)),
  };
}

function createScanCanvas(image) {
  const sourceWidth = image.width || image.naturalWidth;
  const sourceHeight = image.height || image.naturalHeight;
  const dimensions = getPersonScanDimensions(sourceWidth, sourceHeight);
  const canvas = document.createElement('canvas');
  canvas.width = dimensions.width;
  canvas.height = dimensions.height;
  canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function createDetectionTiles(canvas) {
  const tileWidth = Math.max(1, Math.round(canvas.width * .5));
  const tileHeight = Math.max(1, Math.round(canvas.height * .75));
  const xPositions = [0, Math.round((canvas.width - tileWidth) / 2), canvas.width - tileWidth];
  const yPositions = [0, canvas.height - tileHeight];
  return xPositions.flatMap(left => yPositions.map(top => {
    const tile = document.createElement('canvas');
    tile.width = Math.min(PERSON_SCAN_MAX_EDGE, tileWidth * TILE_SCALE);
    tile.height = Math.min(PERSON_SCAN_MAX_EDGE, tileHeight * TILE_SCALE);
    tile.getContext('2d').drawImage(canvas, left, top, tileWidth, tileHeight, 0, 0, tile.width, tile.height);
    return { canvas: tile, left, top, width: tileWidth, height: tileHeight };
  }));
}

function mapTileGeometry(geometry, tile, sourceWidth, sourceHeight) {
  const mapPoint = point => ({
    x: (tile.left + point.x * tile.width) / sourceWidth,
    y: (tile.top + point.y * tile.height) / sourceHeight,
  });
  return {
    ...geometry,
    bounds: {
      left: (tile.left + geometry.bounds.left * tile.width) / sourceWidth,
      top: (tile.top + geometry.bounds.top * tile.height) / sourceHeight,
      right: (tile.left + geometry.bounds.right * tile.width) / sourceWidth,
      bottom: (tile.top + geometry.bounds.bottom * tile.height) / sourceHeight,
    },
    keypoints: geometry.keypoints.map(mapPoint),
  };
}

function boundsOverlap(left, right) {
  const intersectionWidth = Math.max(0, Math.min(left.right, right.right) - Math.max(left.left, right.left));
  const intersectionHeight = Math.max(0, Math.min(left.bottom, right.bottom) - Math.max(left.top, right.top));
  const intersection = intersectionWidth * intersectionHeight;
  const leftArea = (left.right - left.left) * (left.bottom - left.top);
  const rightArea = (right.right - right.left) * (right.bottom - right.top);
  return intersection / Math.max(1e-8, leftArea + rightArea - intersection);
}

function deduplicateGeometries(geometries) {
  return geometries.reduce((unique, geometry) => {
    if (!unique.some(existing => boundsOverlap(existing.bounds, geometry.bounds) >= .3)) unique.push(geometry);
    return unique;
  }, []).map((geometry, index) => ({ ...geometry, index }));
}

async function detectPeopleGeometry(canvas) {
  // The landmarker can return the same face twice in one pass, so whole-image
  // results go through the same overlap filter as the tiled fallback.
  const geometries = deduplicateGeometries(await detectFaceGeometry(canvas, 50));
  if (geometries.length) return geometries;

  const tiledGeometries = [];
  for (const tile of createDetectionTiles(canvas)) {
    const tileResults = await detectFaceGeometry(tile.canvas, 50);
    tiledGeometries.push(...tileResults.map(geometry => mapTileGeometry(
      geometry, tile, canvas.width, canvas.height
    )));
  }
  return deduplicateGeometries(tiledGeometries);
}

async function scanPicturePeople(picture) {
  const file = await downloadPictureForAnalysis(picture, { preferLarge: true });
  const image = await decodeImage(file);
  const canvas = createScanCanvas(image);
  image.close?.();
  const geometries = await detectPeopleGeometry(canvas);
  const faces = [];
  let skippedSmallFaces = 0;
  for (const geometry of geometries) {
    const width = (geometry.bounds.right - geometry.bounds.left) * canvas.width;
    const height = (geometry.bounds.bottom - geometry.bounds.top) * canvas.height;
    if (Math.min(width, height) < MIN_FACE_EDGE) {
      skippedSmallFaces += 1;
      continue;
    }
    const embedding = await extractFaceEmbedding(canvas, geometry.keypoints);
    faces.push({
      pictureId: picture.id,
      faceIndex: geometry.index,
      bounds: geometry.bounds,
      faceArea: width * height,
      embedding,
    });
  }
  return { faces, detectedFaces: geometries.length, skippedSmallFaces };
}

export {
  MIN_FACE_EDGE,
  PERSON_SCAN_MAX_EDGE,
  PERSON_SCAN_MIN_EDGE,
  deduplicateGeometries,
  detectPeopleGeometry,
  getPersonScanDimensions,
  mapTileGeometry,
  scanPicturePeople,
};
